import { useEffect, useState } from "react";
import { Mail, MessageCircle, Send, Instagram, ArrowRight, Clock, CalendarDays } from "lucide-react";
import { useLang } from "../../lib/LangContext";
import { p22Copy } from "../../lib/p22Copy";
import { CONTACT_INFO, buildWhatsAppLink } from "../../lib/translations";
import { BookingWizard } from "../booking/BookingWizard";
import { BookingForm } from "./BookingForm";
import { Reveal } from "../home/Story";

const Channel = ({ href, icon: Icon, label, value, testid, external = true }) => (
  <a data-testid={testid} href={href} {...(external ? { target: "_blank", rel: "noreferrer" } : {})}
    className="group flex items-center gap-4 py-4 border-b border-white/[0.08] hover:border-gold/40 transition-colors">
    <Icon size={17} className="text-gold shrink-0" />
    <span className="flex-1 min-w-0">
      <span className="block text-[11px] tracking-[0.25em] uppercase text-zinc-500">{label}</span>
      <span className="block text-[15px] text-white truncate">{value}</span>
    </span>
    <ArrowRight size={15} className="text-zinc-600 group-hover:text-gold group-hover:translate-x-1 transition-[color,transform]" />
  </a>
);

export const Contact = () => {
  const { lang } = useLang();
  const p = p22Copy(lang);
  const c = p.booking;
  const [legacy, setLegacy] = useState(false);

  useEffect(() => {
    const handler = () => setLegacy(false);
    window.addEventListener("puregloss:set-flow", handler);
    window.addEventListener("puregloss:select-city", handler);
    return () => { window.removeEventListener("puregloss:set-flow", handler); window.removeEventListener("puregloss:select-city", handler); };
  }, []);

  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 md:py-28 bg-[#050505] border-t border-white/[0.06]">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-6 lg:px-12">
        <div className="max-w-3xl">
          <Reveal><span className="text-[12px] tracking-[0.3em] uppercase text-gold">{c.eyebrow}</span></Reveal>
          <Reveal delay={0.08} as="h2" className="mt-5 font-heading text-white text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">{c.title}</Reveal>
          <Reveal delay={0.14} as="p" className="mt-5 text-zinc-300 text-[15px] md:text-base leading-relaxed">{c.subtitle}</Reveal>
          <Reveal delay={0.2}>
            <p data-testid="contact-trust-badge" className="mt-6 inline-flex items-center gap-3 border border-gold/30 bg-gold/[0.04] px-4 py-2.5 text-[13px] text-gold">
              <Clock size={15} /> {c.response}
            </p>
          </Reveal>
        </div>

        <div className="mt-10 md:mt-14 grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-8">
            {legacy ? <BookingForm /> : <BookingWizard />}
            <button type="button" data-testid="contact-toggle-live-slots" onClick={() => setLegacy((v) => !v)}
              className="mt-5 inline-flex items-center gap-2 text-[13px] text-zinc-400 hover:text-gold transition-colors">
              <CalendarDays size={15} /> {legacy ? c.backToWizard : c.liveSlots}
            </button>
          </div>
          <aside className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="border-t border-white/[0.08]">
              <Channel testid="contact-whatsapp-link" href={buildWhatsAppLink("Hi PUREGLOSS! ")} icon={MessageCircle} label="WhatsApp" value={CONTACT_INFO.whatsappDisplay} />
              <Channel testid="contact-email-link" href={`mailto:${CONTACT_INFO.email}`} icon={Mail} label="Email" value={CONTACT_INFO.email} external={false} />
              <Channel testid="contact-instagram-link" href={CONTACT_INFO.instagramUrl} icon={Instagram} label="Instagram" value={CONTACT_INFO.instagram} />
              <Channel testid="contact-telegram-link" href={CONTACT_INFO.telegramUrl} icon={Send} label="Telegram" value={CONTACT_INFO.telegram} />
            </div>
            <p data-testid="contact-area-text" className="mt-6 text-[14px] text-zinc-400 leading-relaxed">{p.area.short}</p>
          </aside>
        </div>
      </div>
    </section>
  );
};
